import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, FileCheck2, Loader2, RefreshCw, Filter, Download,
  FileText, Undo2, ExternalLink, Copy, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fmtServerDateTime as fmtTime } from './utils/datetime';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  issued:       { label: 'Issued',       cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  draft:        { label: 'Draft',        cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  cancelled:    { label: 'Cancelled',    cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  credit_noted: { label: 'Credit noted', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
};

const SUPPLY_BADGES = {
  B2B:  { label: 'B2B',  cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
  B2CS: { label: 'B2C',  cls: 'bg-slate-700/50 text-slate-300 border-slate-600' },
  B2CL: { label: 'B2CL', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/30' },
};

function money(n) {
  const v = Number(n);
  if (Number.isNaN(v)) return '—';
  return v.toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
}

function useTab() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('subtab') || 'invoices';
  const set = (v) => setParams((p) => { p.set('subtab', v); return p; }, { replace: true });
  return [tab, set];
}

export function AdminInvoicingPage() {
  const { admin, loading } = useAdminAuth();
  const [tab, setTab] = useTab();

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div
      data-testid="admin-invoicing-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin?tab=invoicing" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="invoicing-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <FileCheck2 className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">SuperAdmin Invoicing</div>
            <div className="text-xs text-slate-400">Licence-purchase invoices, credit notes, and monthly GSTR-1 / GSTR-3B reports</div>
          </div>
          <Link
            to="/admin/billing-profile"
            data-testid="invoicing-open-billing-profile"
            className="text-xs text-amber-300 hover:text-amber-200 border border-slate-700 rounded-md px-3 py-1.5 hover:border-amber-500/40"
          >
            Company profile <ExternalLink className="inline w-3 h-3 ml-1" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <nav className="inline-flex gap-1 bg-slate-900/70 border border-slate-800 rounded-xl p-1" data-testid="invoicing-tabs">
          {[
            { k: 'invoices', label: 'Invoices' },
            { k: 'credit_notes', label: 'Credit notes' },
            { k: 'reports', label: 'GST reports' },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              data-testid={`invoicing-tab-${t.k}`}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.k ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30'
                  : 'text-slate-300 hover:bg-slate-800/60 hover:text-white border border-transparent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === 'invoices' && <InvoicesTable />}
        {tab === 'credit_notes' && <CreditNotesTable />}
        {tab === 'reports' && <ReportsPanel />}
      </main>
    </div>
  );
}


function InvoicesTable() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [supply, setSupply] = useState('');

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (search.trim()) params.set('search', search.trim());
      if (status) params.set('status', status);
      if (supply) params.set('supply_type', supply);
      const r = await adminApi.get(`/admin/invoicing/invoices?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch { toast.error('Could not load invoices'); }
    finally { setFetching(false); }
  }, [page, pageSize, search, status, supply]);

  useEffect(() => { refresh(); }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="space-y-4">
      <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs text-slate-400 uppercase tracking-wider">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        <select
          data-testid="invoices-status-filter"
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
        >
          <option value="">All statuses</option>
          {Object.entries(STATUS_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select
          data-testid="invoices-supply-filter"
          value={supply}
          onChange={(e) => { setSupply(e.target.value); setPage(1); }}
          className="bg-slate-800/50 border border-slate-700 text-white text-xs rounded-md px-2 py-1.5"
        >
          <option value="">All supply</option>
          {Object.entries(SUPPLY_BADGES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <Input
          data-testid="invoices-search"
          placeholder="Search inv #, buyer name/email/GSTIN, licence, pay ref…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); refresh(); } }}
          className="bg-slate-800/50 border-slate-700 text-white text-sm h-9 max-w-md"
        />
        <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="invoices-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 ml-auto">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <span className="text-xs text-slate-500">
          {total.toLocaleString('en-IN')} invoice{total === 1 ? '' : 's'}
        </span>
      </div>

      <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
        {fetching ? (
          <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No invoices match your filters yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-slate-950/60">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-2 text-left w-36">Invoice #</th>
                <th className="px-4 py-2 text-left w-24">Date</th>
                <th className="px-4 py-2 text-left">Buyer</th>
                <th className="px-4 py-2 text-left w-20">Supply</th>
                <th className="px-4 py-2 text-right w-28">Total</th>
                <th className="px-4 py-2 text-left w-28">Status</th>
                <th className="px-4 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {items.map((row) => {
                const statusBadge = STATUS_BADGES[row.status] || STATUS_BADGES.draft;
                const supplyBadge = SUPPLY_BADGES[row.supply_type] || SUPPLY_BADGES.B2CS;
                return (
                  <tr key={row.id} className="text-slate-200 hover:bg-slate-800/30" data-testid={`invoice-row-${row.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-amber-300 truncate">{row.invoice_number || 'DRAFT'}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{row.invoice_date}</td>
                    <td className="px-4 py-2.5">
                      <div className="text-white text-sm font-medium truncate max-w-md">{row.buyer_name || '—'}</div>
                      <div className="text-xs text-slate-500 truncate max-w-md">
                        {row.buyer_email}
                        {row.buyer_gstin && (
                          <span className="ml-2 font-mono text-slate-400">· {row.buyer_gstin}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${supplyBadge.cls}`}>{supplyBadge.label}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-white text-sm">{money(row.grand_total)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${statusBadge.cls}`}>{statusBadge.label}</span>
                    </td>
                    <td className="px-2 py-2.5 text-slate-500">
                      <Link to={`/admin/invoicing/invoices/${row.id}`} data-testid={`invoice-open-${row.id}`}>
                        <ChevronRight className="w-4 h-4 hover:text-amber-300" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
            <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
          </div>
        </div>
      )}
    </section>
  );
}


function CreditNotesTable() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState('');

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      if (search.trim()) params.set('search', search.trim());
      const r = await adminApi.get(`/admin/invoicing/credit-notes?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch { toast.error('Could not load credit notes'); }
    finally { setFetching(false); }
  }, [page, pageSize, search]);

  useEffect(() => { refresh(); }, [refresh]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section className="space-y-4">
      <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
        <Input
          data-testid="cn-search"
          placeholder="Search CN #, invoice #, buyer, reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); refresh(); } }}
          className="bg-slate-800/50 border-slate-700 text-white text-sm h-9 max-w-md"
        />
        <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="cn-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 ml-auto">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <span className="text-xs text-slate-500">
          {total.toLocaleString('en-IN')} credit note{total === 1 ? '' : 's'}
        </span>
      </div>

      <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden">
        {fetching ? (
          <div className="p-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            No credit notes yet. They're auto-created when you issue one from an invoice.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="cn-table">
            <thead className="bg-slate-950/60">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-2 text-left w-40">Credit note #</th>
                <th className="px-4 py-2 text-left w-24">Date</th>
                <th className="px-4 py-2 text-left w-40">Against invoice</th>
                <th className="px-4 py-2 text-left">Buyer / Reason</th>
                <th className="px-4 py-2 text-right w-28">Amount</th>
                <th className="px-4 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {items.map((row) => (
                <tr key={row.id} className="text-slate-200 hover:bg-slate-800/30" data-testid={`cn-row-${row.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs text-rose-300">{row.credit_note_number}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-400 whitespace-nowrap">{row.issue_date}</td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/admin/invoicing/invoices/${row.original_invoice_id}`}
                      className="font-mono text-xs text-amber-300 hover:text-amber-200"
                      data-testid={`cn-parent-${row.id}`}
                    >
                      {row.original_invoice_number}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-white text-sm truncate">{row.buyer_name}</div>
                    <div className="text-xs text-slate-500 truncate">{row.reason}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-rose-300 text-sm">-{money(row.grand_total)}</td>
                  <td className="px-2 py-2.5">
                    <a
                      href={`${BACKEND}/api/admin/invoicing/credit-notes/${row.id}/pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-slate-500 hover:text-amber-300"
                      data-testid={`cn-pdf-${row.id}`}
                    >
                      <FileText className="w-4 h-4" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-between text-xs text-slate-400">
          <div>Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
            <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || fetching} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
          </div>
        </div>
      )}
    </section>
  );
}


function ReportsPanel() {
  const now = new Date();
  const [fy, setFy] = useState(() => {
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${(y % 100).toString().padStart(2, '0')}-${((y + 1) % 100).toString().padStart(2, '0')}`;
  });
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [preview, setPreview] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [previewKind, setPreviewKind] = useState('gstr1');

  const fyOptions = useMemo(() => {
    const y = now.getFullYear();
    const opts = [];
    for (let i = -1; i < 3; i += 1) {
      const s = y - 1 + i;
      opts.push(`${(s % 100).toString().padStart(2, '0')}-${((s + 1) % 100).toString().padStart(2, '0')}`);
    }
    return opts.reverse();
  }, [now]);

  const loadJson = async (kind) => {
    setFetching(true);
    setPreviewKind(kind);
    try {
      const r = await adminApi.get(`/admin/invoicing/reports/${kind}?fy=${fy}&month=${month}`);
      setPreview(r.data);
    } catch { toast.error('Report generation failed'); }
    finally { setFetching(false); }
  };

  const downloadXlsx = async (kind) => {
    try {
      const r = await adminApi.get(`/admin/invoicing/reports/${kind}?fy=${fy}&month=${month}&format=xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${kind.toUpperCase()}-${fy}-${String(month).padStart(2, '0')}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Downloaded');
    } catch { toast.error('Download failed'); }
  };

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(preview, null, 2));
      toast.success('JSON copied');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <section className="space-y-5">
      <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Financial Year</label>
            <select
              data-testid="report-fy"
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              className="block mt-1 bg-slate-800/50 border border-slate-700 text-white text-sm rounded-md px-3 py-1.5"
            >
              {fyOptions.map((v) => <option key={v} value={v}>FY 20{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Month</label>
            <select
              data-testid="report-month"
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="block mt-1 bg-slate-800/50 border border-slate-700 text-white text-sm rounded-md px-3 py-1.5"
            >
              {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReportCard
            title="GSTR-1"
            description="B2B, B2CL, B2CS supplies and credit notes for the selected month. Preview JSON in-place or download the Excel workbook."
            testId="report-gstr1"
            onPreview={() => loadJson('gstr1')}
            onDownload={() => downloadXlsx('gstr1')}
          />
          <ReportCard
            title="GSTR-3B"
            description="3.1(a) outward-taxable-supplies summary — the row you type into the monthly GST portal filing."
            testId="report-gstr3b"
            onPreview={() => loadJson('gstr3b')}
            onDownload={() => downloadXlsx('gstr3b')}
          />
        </div>
      </div>

      {(fetching || preview) && (
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden" data-testid="report-preview">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5">
            <div className="text-slate-300 text-sm font-medium">{previewKind.toUpperCase()} preview · FY 20{fy} · Month {month}</div>
            {preview && (
              <Button onClick={copyJson} variant="outline" size="sm" data-testid="report-copy-json" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy JSON
              </Button>
            )}
          </div>
          <pre className="p-4 text-[11px] text-emerald-200 overflow-auto max-h-[60vh]">
            {fetching ? 'Loading…' : JSON.stringify(preview, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}


function ReportCard({ title, description, testId, onPreview, onDownload }) {
  return (
    <div className="bg-slate-800/40 border border-slate-700 rounded-xl p-5 space-y-3" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
          <FileCheck2 className="w-5 h-5 text-amber-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">{title}</h3>
          <p className="text-xs text-slate-400 mt-1">{description}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={onPreview} variant="outline" size="sm" data-testid={`${testId}-preview`} className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
          <FileText className="w-3.5 h-3.5 mr-1.5" /> Preview JSON
        </Button>
        <Button onClick={onDownload} size="sm" data-testid={`${testId}-xlsx`} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Excel
        </Button>
      </div>
    </div>
  );
}

// Named re-export helps Undo2 icon tree-shake stays happy (avoids unused
// warning at build; the icon is used inside the detail page).
export { Undo2 as _UndoIcon };
